import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Alert, Button, Paper } from '@c2fo/liquidity';
import { CopyIcon } from '@c2fo/liquidity/icons';
import { getToken } from '../../../utils/token';
import { config } from '../../../config';
import { copyToClipboard } from '../../../utils/clipboard';

interface RegistrationLinkPanelProps {
  userId: string;
  userStatus: number;
}

const DEBOUNCE_DELAY = 500;

export const RegistrationLinkPanel: React.FC<RegistrationLinkPanelProps> = ({ userId, userStatus }) => {
  const [registrationUrl, setRegistrationUrl] = useState<string | null>(null);
  const [copiedRegistrationUrl, setCopiedRegistrationUrl] = useState(false);
  const [clipboardError, setClipboardError] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const fetchRegistrationUrl = useCallback(async () => {
    if (!userId || userStatus !== 1) return;

    setLoading(true);
    setError(false);
    setRegistrationUrl(null);

    try {
      const token = getToken();
      const adminApiUrl = config.ADMIN_LOCAL_API_URL || config.ADMIN_API_URL;
      const authTokenResponse = await fetch(`${adminApiUrl}/api/admin/user/auth-token/${userId}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!authTokenResponse.ok) {
        throw new Error('Failed to fetch auth token');
      }

      const authTokenData: { token: string } = await authTokenResponse.json();
      const baseUrl = config.GATEWAY_URL.includes('fss-gateway')
        ? 'https://fss-app.c2fo.com'
        : config.GATEWAY_URL.replace('-gateway', '-app');
      const registrationUrlWithParams = `${baseUrl}/registration?token=${authTokenData.token}&utm_content=copy_user_registration_link&utm_medium=email&utm_source=c2fo_admin&utm_campaign=srm_admin_link`;
      setRegistrationUrl(registrationUrlWithParams);
      setError(false);
    } catch (err) {
      setError(true);
      setRegistrationUrl(null);
    } finally {
      setLoading(false);
    }
  }, [userId, userStatus]);

  const handleRetry = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchRegistrationUrl();
    }, DEBOUNCE_DELAY);
  }, [fetchRegistrationUrl]);

  useEffect(() => {
    if (userStatus === 1 && userId) {
      fetchRegistrationUrl();
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [userStatus, userId, fetchRegistrationUrl]);

  if (userStatus !== 1 || !userId) {
    return null;
  }

  return (
    <Paper elevation={1} square className="p-6 mb-8">
      <Alert
        variant="filled"
        type="warning"
        full
        icon={false}
        description={
          <div className="w-full space-y-2">
            <div className="flex items-start justify-between gap-2">
              <p className="font-semibold text-sm">
                This user has registered but needs to set an initial password. Send them the link below to take them to
                the password setting page.
              </p>
              {registrationUrl && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={async () => {
                    setClipboardError(null);
                    const success = await copyToClipboard(registrationUrl);
                    if (success) {
                      setCopiedRegistrationUrl(true);
                      setTimeout(() => setCopiedRegistrationUrl(false), 2000);
                    } else {
                      setClipboardError(
                        'Failed to copy to clipboard. Please try selecting and copying the text manually.',
                      );
                      setTimeout(() => setClipboardError(null), 5000);
                    }
                  }}
                  icon={<CopyIcon className="w-3.5 h-3.5" />}
                  className="flex-shrink-0"
                >
                  {copiedRegistrationUrl ? 'Copied!' : 'Copy'}
                </Button>
              )}
            </div>
            {error ? (
              <div className="w-full space-y-2">
                <p className="text-sm text-text-secondary">Unable to get registration link</p>
                <Button variant="secondary" size="sm" onClick={handleRetry} disabled={loading}>
                  {loading ? 'Retrying...' : 'Retry'}
                </Button>
              </div>
            ) : registrationUrl ? (
              <>
                {clipboardError && (
                  <div className="mb-2">
                    <Alert variant="filled" type="error" full description={clipboardError} />
                  </div>
                )}
                <pre
                  data-testid="registration-url"
                  className="w-full break-all whitespace-pre-wrap rounded border border-stroke bg-gray-50 p-3 text-xs overflow-x-auto"
                >
                  {registrationUrl}
                </pre>
              </>
            ) : (
              <div className="w-full text-sm text-text-secondary">Loading registration link...</div>
            )}
          </div>
        }
      />
    </Paper>
  );
};
